import { ArrowDown, SearchIcon } from "lucide-react";
import styles from "./Payment.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import { handleGetPaymentJobDetailsList } from "~/thunks/job-details/jobDetailsThunk";
import { notify } from "~/utils/helper";
import { IInvoiceJobDetails, IPaymentInFinancialTab } from "~/utils/interface/jobDetails";
import { ColumnType } from "~/utils/types/common";
import { ActionIcon } from "~/assets/icons";
import dayjs from "dayjs";
import LogPaymentModal from "../log-payment-modal/LogPaymentModal";
import ProcessPaymentModal from "../process-payment-modal/ProcessPaymentModal";
import RequestPaymentModal from "../request-payment-modal/RequestPaymentModal";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import jobDetailsApi from "~/apis/jobDetails";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";

const cx = classNames.bind(styles);

interface PaymentProps {
  jobId: string;
  getListPayment: boolean;
  setGetListPayment: (value: boolean) => void;
}


const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Draft", value: "Draft" },
  { label: "Sent", value: "Sent" },
  { label: "Open", value: "Open" },
  { label: "Closed", value: "Closed" },
  { label: "Cancelled", value: "Cancelled" },
  { label: "Void", value: "Void" },
];


const Payment = ({
  jobId,
  getListPayment,
  setGetListPayment,
}: PaymentProps) => {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { paymentJobDetailsList } = useAppSelector((state) => state.jobDetails);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const activeActionButtonRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const recordPaymentButtonRef = useRef<HTMLDivElement>(null);
  const recordPaymentDropdownRef = useRef<HTMLDivElement>(null);
  const paymentTableRef = useRef<HTMLDivElement>(null);
  const [openLogPaymentModal, setOpenLogPaymentModal] = useState(false);
  const [openProcessPaymentModal, setOpenProcessPaymentModal] = useState(false);
  const [openRequestPaymentModal, setOpenRequestPaymentModal] = useState(false);
  const [openModalAlertDelete, setOpenModalAlertDelete] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [editPaymentId, setEditPaymentId] = useState<string | null>(null);
  const [paymentMode, setPaymentMode] = useState<"create" | "edit">("create");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showItemsDropdown, setShowItemsDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemsPerPageOptions = [10, 20, 50, 100];


  const [formData, setFormData] = useState({
    status: "",
  });


  const handleFieldChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

  };

  const calculateDropdownPosition = () => {
    if (recordPaymentButtonRef.current) {
      const rect = recordPaymentButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const dropdownWidth = 200;
      const spacing = 4;
      const padding = 8;

      let left = rect.left;
      if (left + dropdownWidth > viewportWidth - padding) {
        left = Math.max(padding, viewportWidth - dropdownWidth - padding);
      }
      if (left < padding) {
        left = padding;
      }

      const spaceBelow = viewportHeight - rect.bottom - spacing - padding;
      const spaceAbove = rect.top - spacing - padding;

      let top: number;
      if (spaceBelow < 150 && spaceAbove > spaceBelow) {
        top = rect.top - 150 - spacing;
        if (top < padding) {
          top = padding;
        }
      } else {
        top = rect.bottom + spacing;
        if (top + 150 > viewportHeight - padding) {
          top = Math.max(padding, rect.top - 150 - spacing);
        }
      }

      return { top, left };
    }
    return null;
  };

  const handleToggleRecordPaymentDropdown = () => {
    if (!isDropdownOpen) {
      requestAnimationFrame(() => {
        const position = calculateDropdownPosition();
        if (position) {
          setDropdownPosition(position);
          setIsDropdownOpen(true);
        }
      });
    } else {
      setIsDropdownOpen(false);
      setDropdownPosition(null);
    }
  };

  const handleRequestPayment = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    setOpenRequestPaymentModal(true);
  };

  const handleProcessPaymentNow = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    setOpenProcessPaymentModal(true);
  };

  const handleLogPayment = () => {
    setIsDropdownOpen(false);
    setDropdownPosition(null);
    setEditPaymentId(null);
    setPaymentMode("create");
    setOpenLogPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setGetListPayment(true);
  };

  useEffect(() => {
    if (jobId || getListPayment === true) {
      setIsLoading(true);
      dispatch(
        handleGetPaymentJobDetailsList({
          idJob: jobId,
        })
      )
        .unwrap()
        .then((res) => {
          setIsLoading(false);
          if (getListPayment === true) {
            setGetListPayment(false);
          }
        })
        .catch((error: any) => {
          notify(
            "error",
            `Failed to fetch payment list: ${error.response.data.message}`
          );
          setIsLoading(false);
          if (getListPayment === true) {
            setGetListPayment(false);
          }
        });
    }
  }, [dispatch, jobId, getListPayment]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (!showItemsDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowItemsDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showItemsDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        const target = event.target as Node;
        if (
          recordPaymentButtonRef.current &&
          !recordPaymentButtonRef.current.contains(target) &&
          recordPaymentDropdownRef.current &&
          !recordPaymentDropdownRef.current.contains(target)
        ) {
          setIsDropdownOpen(false);
          setDropdownPosition(null);
        }
      }
    };

    const handleScroll = () => {
      if (isDropdownOpen) {
        requestAnimationFrame(() => {
          const position = calculateDropdownPosition();
          if (position) {
            setDropdownPosition(position);
          } else {
            setIsDropdownOpen(false);
            setDropdownPosition(null);
          }
        });
      }

      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleTableScroll = () => {
      if (activeMenu) {
        setActiveMenu(null);
        activeActionButtonRef.current = null;
      }
    };

    const handleResize = () => {
      if (isDropdownOpen) {
        requestAnimationFrame(() => {
          const position = calculateDropdownPosition();
          if (position) {
            setDropdownPosition(position);
          } else {
            setIsDropdownOpen(false);
            setDropdownPosition(null);
          }
        });
      }

      if (activeMenu && activeActionButtonRef.current) {
        requestAnimationFrame(() => {
          const position = calculateActionMenuPosition(
            activeActionButtonRef.current!
          );
          setMenuPosition(position);
        });
      }
    };

    const tableElement = paymentTableRef.current;

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("resize", handleResize);

    if (tableElement) {
      tableElement.addEventListener("scroll", handleTableScroll, {
        passive: true,
      });
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      document.removeEventListener("scroll", handleScroll, {
        capture: true,
      } as EventListenerOptions);
      window.removeEventListener("resize", handleResize);
      if (tableElement) {
        tableElement.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [isDropdownOpen, activeMenu]);

  const filteredPayments = useMemo(() => {
    let filtered = paymentJobDetailsList?.responses || [];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((payment: IPaymentInFinancialTab) => {
        const paymentDate = payment.paymentDate?.toLowerCase() || "";
        const reference = payment.reference?.toLowerCase() || "";
        const total = payment.paymentAmount?.toLowerCase() || "";
        const invoices =
          payment.invoice?.invoiceUniqueCode?.toLowerCase() || "";
        const method = payment.paymentMethod?.toLowerCase() || "";
        const relatedRecords = "Null";
        const createdDate = payment.createdAt?.toLowerCase() || "";
        const dateUpdated = payment.updatedAt?.toLowerCase() || "";
        const status = payment.status?.toLowerCase() || "";

        return (
          paymentDate.includes(query) ||
          reference.includes(query) ||
          total.includes(query) ||
          invoices.includes(query) ||
          method.includes(query) ||
          relatedRecords.includes(query) ||
          createdDate.includes(query) ||
          dateUpdated.includes(query) ||
          status.includes(query)
        );
      });
    }

    if (formData.status) {
      filtered = filtered.filter(
        (invoice: IInvoiceJobDetails) =>
          (invoice.status || "").toLowerCase() === formData.status.toLowerCase()
      );
    }

    return filtered;
  }, [paymentJobDetailsList?.responses, searchQuery]);

  const totalItems = filteredPayments.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleChangePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setPage(1);
    setShowItemsDropdown(false);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", page - 1, page, page + 1, "...", totalPages);
      }
    }

    return pages.filter((p, idx, arr) => !(p === arr[idx - 1]));
  };

  const paginatedPayments = useMemo(() => {
    return filteredPayments.slice(
      (page - 1) * itemsPerPage,
      page * itemsPerPage
    );
  }, [filteredPayments, page, itemsPerPage]);

  const showingCount = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const remaining = Math.max(totalItems - startIndex, 0);
    return Math.min(itemsPerPage, remaining);
  }, [page, itemsPerPage, totalItems]);

  const calculateActionMenuPosition = (buttonElement: HTMLDivElement) => {
    const rect = buttonElement.getBoundingClientRect();
    return {
      top: rect.bottom + 5,
      left: rect.left - 100,
    };
  };

  const handleActionClick = (
    event: React.MouseEvent,
    payment: IPaymentInFinancialTab
  ) => {
    event.stopPropagation();

    if (activeMenu === payment.id) {
      setActiveMenu(null);
      activeActionButtonRef.current = null;
      return;
    }

    const buttonElement = event.currentTarget as HTMLDivElement;
    activeActionButtonRef.current = buttonElement;
    const position = calculateActionMenuPosition(buttonElement);
    setMenuPosition(position);
    setActiveMenu(payment.id);
  };

  const ActionMenu = ({ payment, onClose, position }: any) => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleEdit = () => {
      setEditPaymentId(payment.id);
      setPaymentMode("edit");
      setOpenLogPaymentModal(true);
      onClose();
    };

    const handleDelete = () => {
      setOpenModalAlertDelete(true);
      setPaymentId(payment.id);
    };

    return (
      <div
        ref={menuRef}
        className={cx("action-menu")}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        {payment.isStripePayment ? (
          <button className={cx("menu-item")} disabled title="Refund card payments in Stripe">
            Stripe card payment (read-only)
          </button>
        ) : (
          <>
            <button className={cx("menu-item")} onClick={handleEdit}>
              Edit
            </button>

            <button className={cx("menu-item")} onClick={handleDelete}>
              Delete
            </button>
          </>
        )}
      </div>
    );
  };

  const statusClass = (status: string) => {
    switch (status) {
      case "Processing":
        return "processing";
      case "Completed":
        return "completed";
      default:
        return "null";
    }
  };

  const handleDeletePayment = async () => {
    try {
      setIsLoading(true);
      await jobDetailsApi.deletePayment(jobId, paymentId || "");
      setIsLoading(false);
      notify("success", "Delete payment successfully");
      setOpenModalAlertDelete(false);
      setPaymentId(null);
      setGetListPayment(true);
    } catch (error: any) {
      notify("error", error.response.data.message || "Failed to delete payment");
    }
  };

  return (
    <div className={cx("payment-container")}>
      <div className={cx("payment-header")}>
        <div className={cx("search-container")}>
          <SearchIcon className={cx("search-icon")} />
          <input
            type="text"
            placeholder="Search payment"
            className={cx("search-input")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={cx("filter-container")}>
          <PrimarySelectInput
            name="filter"
            placeholder="Select"
            value={formData.status}
            onChange={(value) => handleFieldChange("status", value)}
            options={STATUS_OPTIONS.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            className={cx("input")}
            classNameIcon={cx("select-input-icon")}
          />
          <div
            ref={recordPaymentButtonRef}
            className={cx("record-payment-wrapper")}
          >
            <PrimaryButton
              title="Record Payment"
              className={cx("create-button")}
              onClick={handleToggleRecordPaymentDropdown}
            />
            {isDropdownOpen && dropdownPosition && (
              <div
                ref={recordPaymentDropdownRef}
                className={cx("payment-dropdown")}
                data-payment-dropdown
                style={{
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                }}
              >
                <div
                  className={cx("dropdown-item")}
                  onClick={handleRequestPayment}
                >
                  Request Payment
                </div>
                <div
                  className={cx("dropdown-item")}
                  onClick={handleProcessPaymentNow}
                >
                  Process Payment Now
                </div>
                <div className={cx("dropdown-item")} onClick={handleLogPayment}>
                  Log Payment
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={cx("payment-table")}>
        {isLoading ? (
          <div className={cx("loading-overlay")}>
            <LoadingMainContent />
          </div>
        ) : (
          <>
            <div ref={paymentTableRef} className={cx("table-wrapper")}>
              <table className={cx("sections-table")}>
                <thead>
                  <tr>
                    <th className={cx("col-payment-date")}>Payment Date</th>
                    <th className={cx("col-reference")}>Reference</th>
                    <th className={cx("col-total")}>Total</th>
                    <th className={cx("col-invoice")}>Invoice</th>
                    <th className={cx("col-method")}>Method</th>
                    <th className={cx("col-related-records")}>Related Records</th>
                    <th className={cx("col-created-date")}>Created Date</th>
                    <th className={cx("col-date-updated")}>Date Updated</th>
                    <th className={cx("col-status")}>Status</th>
                    <th className={cx("col-actions")}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.map((payment: IPaymentInFinancialTab) => (
                    <tr key={payment.id} className={cx("item-row")}>
                      <td className={cx("col-payment-date")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment.paymentDate
                              ? dayjs(payment.paymentDate).format("YYYY-MM-DD")
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-reference")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment.reference || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-total")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            ${payment?.paymentAmount || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-invoice")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment?.paymentLogs
                              ?.map((log) => log.invoice?.invoiceUniqueCode)
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-method")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment.paymentMethod || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-related-records")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment?.job?.jobUniqueID || "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-created-date")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment.createdAt
                              ? dayjs(payment.createdAt).format("YYYY-MM-DD")
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-date-updated")}>
                        <div className={cx("cell")}>
                          <p className={cx("text-content")}>
                            {payment.updatedAt
                              ? dayjs(payment.updatedAt).format("YYYY-MM-DD")
                              : "-"}
                          </p>
                        </div>
                      </td>
                      <td className={cx("col-status")}>
                        <div className={cx("cell")}>
                          <div className={cx("status-button-container")}>
                            <div
                              className={cx(
                                "status-button",
                                statusClass(payment.status)
                              )}
                            >
                              <p className={cx("status-text")}>
                                {payment.status || ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={cx("col-actions")}>
                        <div
                          className={cx("action-icon-container")}
                          onClick={(e) => handleActionClick(e, payment)}
                        >
                          <div>
                            <ActionIcon className={cx("action-icon")} />
                          </div>
                          {activeMenu === payment.id && (
                            <ActionMenu
                              payment={payment}
                              onClose={() => {
                                setActiveMenu(null);
                                activeActionButtonRef.current = null;
                              }}
                              position={menuPosition}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={cx("pagination-container")}>
              <div className={cx("pagination-left")}>
                <span className={cx("pagination-text")}>Show result</span>
                <div className={cx("items-per-page-wrapper")} ref={dropdownRef}>
                  <button
                    className={cx("items-per-page-button")}
                    onClick={() => setShowItemsDropdown(!showItemsDropdown)}
                  >
                    <span>{showingCount}</span>
                    <svg
                      width="9"
                      height="9"
                      viewBox="0 0 9 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z"
                        fill="#789EB4"
                      />
                    </svg>
                  </button>
                  {showItemsDropdown && (
                    <div className={cx("items-per-page-dropdown")}>
                      {itemsPerPageOptions.map((option) => (
                        <button
                          key={option}
                          className={cx("dropdown-item", {
                            active: itemsPerPage === option,
                          })}
                          onClick={() => handleItemsPerPageChange(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <span className={cx("pagination-text")}>out of</span>
                <span className={cx("pagination-total")}>{totalItems}</span>
              </div>

              <div className={cx("pagination-right")}>
                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === 1 || page === 0,
                  })}
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 1 || page === 0}
                >
                  <ArrowDown className={cx("nav-icon", "rotate-90")} />
                  <span>Previous</span>
                </button>

                <div className={cx("page-numbers")}>
                  {getPageNumbers().map((pageNum, index) => {
                    if (pageNum === "...") {
                      return (
                        <span
                          key={`ellipsis-${index}`}
                          className={cx("page-ellipsis")}
                        >
                          ...
                        </span>
                      );
                    }
                    const pageNumber = Number(pageNum);
                    const isActive = page === pageNumber;
                    return (
                      <button
                        key={pageNumber}
                        className={cx("page-number", {
                          active: isActive,
                        })}
                        onClick={() => handleChangePage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className={cx("pagination-nav-button", {
                    disabled: page === totalPages,
                  })}
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  <span>Next</span>
                  <ArrowDown className={cx("nav-icon", "!rotate-[270deg]")} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <LogPaymentModal
        idJob={jobId}
        open={openLogPaymentModal}
        onClose={() => {
          setOpenLogPaymentModal(false);
          setEditPaymentId(null);
          setPaymentMode("create");
        }}
        onSuccess={handlePaymentSuccess}
        paymentId={editPaymentId}
        mode={paymentMode}
      />

      <RequestPaymentModal
        jobId={jobId}
        open={openRequestPaymentModal}
        onClose={() => setOpenRequestPaymentModal(false)}
      />

      <ProcessPaymentModal
        open={openProcessPaymentModal}
        onClose={() => setOpenProcessPaymentModal(false)}
        jobId={jobId}
      />

      <ModalAlertDelete
        open={openModalAlertDelete}
        description="Are you sure you want to delete this payment?"
        onCancel={() => setOpenModalAlertDelete(false)}
        onConfirm={handleDeletePayment}
      />
    </div>
  );
};

export default Payment;
